import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, LoginResponse } from '../types';
import { login, logout, refreshToken, setRefreshTokenHandler, clearRefreshTokenHandler, setupSuperAdmin, buildOAuth2LogoutUrl } from '../services/api';
import { parseJWT, generateState } from '../lib/utils';
import { OAUTH2_LOGOUT_REDIRECT_URI, OAUTH2_CLIENT_ID } from '../lib/env';

interface OAuth2Tokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  idToken?: string; // ID token for OIDC logout
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string, orgId?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkSetupRequired: () => Promise<boolean>;
  setupSuperAdmin: (data: { secret: string; email: string; password: string; name?: string }) => Promise<void>;
  clearError: () => void;
  setLoading: (loading: boolean) => void;

  // OAuth2 specific actions
  setTokens: (tokens: OAuth2Tokens) => void;
  setUser: (user: User) => void;
  loginWithTokens: (tokens: OAuth2Tokens, user: User) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      loading: false,
      error: null,

      login: async (email: string, password: string, _orgId?: string) => {
        set({ loading: true, error: null });
        try {
          const response = await login(email, password) as LoginResponse;

          // Handle both JWT-based and session-based responses
          let user: User;

          if (response.access_token) {
            // JWT-based auth (admin login, org user login)
            const jwtPayload = parseJWT(response.access_token);

            if (!jwtPayload || !jwtPayload.sub) {
              throw new Error('Login failed: Invalid token received');
            }

            user = {
              id: jwtPayload.sub,
              email: response.user.email,
              email_verified: response.user.email_verified,
              name: response.user.name,
              role: jwtPayload.role,
              roles: jwtPayload.roles || response.user.roles || [jwtPayload.role],
              is_super_admin: jwtPayload.role === 'super_admin',
              org_id: jwtPayload.org_id, // Include org_id from JWT
              locked: response.user.locked || false,
              locked_until: response.user.locked_until || null,
              created_at: response.user.created_at,
              updated_at: response.user.updated_at,
            };

            // Store tokens in localStorage for API client
            if (response.access_token) {
              localStorage.setItem('oauth_access_token', response.access_token);
            }
            if (response.refresh_token) {
              localStorage.setItem('oauth_refresh_token', response.refresh_token);
              // Calculate expiry from JWT
              const expiry = jwtPayload.exp ? jwtPayload.exp * 1000 : Date.now() + (60 * 60 * 1000); // Default 1 hour
              localStorage.setItem('oauth_token_expiry', expiry.toString());
            }

            // Set up refresh token handler
            setRefreshTokenHandler(async () => {
              const storedRefreshToken = localStorage.getItem('oauth_refresh_token');
              if (!storedRefreshToken) {
                throw new Error('Token refresh failed: No refresh token available');
              }

              const refreshResponse = await refreshToken(storedRefreshToken) as LoginResponse;

              if (!refreshResponse || !refreshResponse.access_token) {
                throw new Error('Token refresh failed: No access token received');
              }

              if (!refreshResponse.refresh_token) {
                throw new Error('Token refresh failed: No refresh token received');
              }

              const newJwtPayload = parseJWT(refreshResponse.access_token);
              if (!newJwtPayload || !newJwtPayload.sub) {
                throw new Error('Token refresh failed: Invalid token received');
              }

              const updatedUser: User = {
                id: newJwtPayload.sub,
                email: refreshResponse.user.email,
                email_verified: refreshResponse.user.email_verified,
                name: refreshResponse.user.name,
                role: newJwtPayload.role,
                roles: newJwtPayload.roles || refreshResponse.user.roles || [newJwtPayload.role],
                is_super_admin: newJwtPayload.role === 'super_admin',
                org_id: newJwtPayload.org_id,
                locked: refreshResponse.user.locked || false,
                locked_until: refreshResponse.user.locked_until || null,
                created_at: refreshResponse.user.created_at,
                updated_at: refreshResponse.user.updated_at,
              };

              // Update stored tokens
              localStorage.setItem('oauth_access_token', refreshResponse.access_token);
              localStorage.setItem('oauth_refresh_token', refreshResponse.refresh_token);
              const newExpiry = newJwtPayload.exp ? newJwtPayload.exp * 1000 : Date.now() + (60 * 60 * 1000);
              localStorage.setItem('oauth_token_expiry', newExpiry.toString());

              set({ user: updatedUser, isAuthenticated: true });

              return refreshResponse.refresh_token;
            });

          } else {
            // Session-based auth (fallback)
            if (!response.user) {
              throw new Error('Login failed: No user data received');
            }
            user = response.user;
            clearRefreshTokenHandler();
            // Clear any stored tokens
            localStorage.removeItem('oauth_access_token');
            localStorage.removeItem('oauth_refresh_token');
            localStorage.removeItem('oauth_token_expiry');
          }

          set({
            user,
            isAuthenticated: true,
            loading: false,
            error: null
          });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Login failed';
          set({
            error: message,
            loading: false,
            isAuthenticated: false,
            user: null
          });
          throw error;
        }
      },

      logout: async () => {
        set({ loading: true });
        try {
          // Get stored tokens
          const storedRefreshToken = localStorage.getItem('oauth_refresh_token');
          const storedIdToken = localStorage.getItem('oauth_id_token');
          
          // Call backend logout - this will revoke tokens and clear local session
          await logout(storedRefreshToken || undefined);
          
          // If we have an ID token, perform RP-initiated logout (OIDC spec)
          // This redirects to the OAuth2 provider to clear the session there too
          if (storedIdToken) {
            // Generate a state parameter for security
            const state = generateState();
            
            // Store the state temporarily to verify when user returns
            sessionStorage.setItem('oauth_logout_state', state);
            
            // Build the OAuth2 logout URL
            const logoutUrl = buildOAuth2LogoutUrl(
              storedIdToken,
              OAUTH2_LOGOUT_REDIRECT_URI,
              OAUTH2_CLIENT_ID,
              state
            );
            
            // Clear local state before redirect
            clearRefreshTokenHandler();
            localStorage.removeItem('auth-storage');
            localStorage.removeItem('oauth_access_token');
            localStorage.removeItem('oauth_refresh_token');
            localStorage.removeItem('oauth_token_expiry');
            localStorage.removeItem('oauth_id_token');
            localStorage.removeItem('csrf_token');
            
            // Redirect to OAuth2 logout endpoint
            window.location.href = logoutUrl;
            return; // Don't continue - we're redirecting
          }
        } catch {
          // Ignore logout errors - still clear local state
        } finally {
          // Only clear state if we didn't redirect (no ID token)
          clearRefreshTokenHandler();
          set({
            user: null,
            isAuthenticated: false,
            loading: false,
            error: null
          });
          // Clear persisted state
          localStorage.removeItem('auth-storage');
          // Clear OAuth tokens
          localStorage.removeItem('oauth_access_token');
          localStorage.removeItem('oauth_refresh_token');
          localStorage.removeItem('oauth_token_expiry');
          localStorage.removeItem('oauth_id_token');
          localStorage.removeItem('csrf_token');
        }
      },

      refreshToken: async () => {
        const currentState = get();
        if (!currentState.user) return;

        set({ loading: true, error: null });
        try {
          // This would be called if we stored refresh token in localStorage
          // But we're using the handler approach
        } catch (error: unknown) {
          set({ error: error instanceof Error ? error.message : 'Token refresh failed', isAuthenticated: false, user: null });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      checkSetupRequired: async (): Promise<boolean> => {
        try {
          // Try to get health or any public endpoint
          // If setup is needed, the API will return specific error
          // For now, we'll check if we can access the setup endpoint
          // This is a placeholder - real implementation depends on backend behavior
          return false; // Assume setup is done, will be checked on actual setup attempt
        } catch {
          return true;
        }
      },

      setupSuperAdmin: async (data: { secret: string; email: string; password: string; name?: string }) => {
        set({ loading: true, error: null });
        try {
          const response = await setupSuperAdmin(data) as LoginResponse;

          // Session-based auth: backend sets httpOnly cookie, response contains user
          // Validate response structure
          if (!response || !response.user) {
            throw new Error('Setup failed: No user data received from server');
          }

          // User object from backend already has all needed fields
          const user: User = {
            id: response.user.id,
            email: response.user.email,
            email_verified: response.user.email_verified,
            name: response.user.name,
            role: response.user.role,
            is_super_admin: response.user.is_super_admin,
            org_id: response.user.org_id,
            locked: response.user.locked || false,
            locked_until: response.user.locked_until || null,
            created_at: response.user.created_at,
            updated_at: response.user.updated_at,
          };

          // Session-based auth doesn't use refresh tokens
          // The session cookie is stored in the browser and sent automatically
          clearRefreshTokenHandler();

          set({ user, isAuthenticated: true, loading: false, error: null });
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Setup failed';
          set({
            error: message,
            loading: false
          });
          throw error;
        }
      },

      clearError: () => set({ error: null }),

      setLoading: (loading: boolean) => set({ loading }),

      // OAuth2 specific methods
      setTokens: (tokens: OAuth2Tokens) => {
        // Store tokens in localStorage for session persistence
        localStorage.setItem('oauth_access_token', tokens.accessToken);
        localStorage.setItem('oauth_refresh_token', tokens.refreshToken);
        localStorage.setItem('oauth_token_expiry', tokens.expiresAt.toString());
        
        // Store ID token for OIDC logout if provided
        if (tokens.idToken) {
          localStorage.setItem('oauth_id_token', tokens.idToken);
        }
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true, loading: false, error: null });
      },

      loginWithTokens: async (tokens: OAuth2Tokens, user: User) => {
        set({ loading: true, error: null });

        try {
          // Validate tokens
          if (!tokens || !tokens.accessToken) {
            throw new Error('OAuth2 login failed: No access token provided');
          }

          // Store tokens
          localStorage.setItem('oauth_access_token', tokens.accessToken);
          localStorage.setItem('oauth_refresh_token', tokens.refreshToken);
          localStorage.setItem('oauth_token_expiry', tokens.expiresAt.toString());
          
          // Store ID token for OIDC logout if provided
          if (tokens.idToken) {
            localStorage.setItem('oauth_id_token', tokens.idToken);
          }

          // Set user and authentication state
          set({
            user,
            isAuthenticated: true,
            loading: false,
            error: null
          });

          // Register refresh token handler for API client
          const refreshTokenHandler = async () => {
            if (!tokens.refreshToken) {
              throw new Error('OAuth2 token refresh failed: No refresh token available');
            }

            // Import here to avoid circular dependency issues
            const { oauthToken } = await import('../services/api');
            // Import the environment variable from env to use the same fallback logic
            const { OAUTH2_CLIENT_ID } = await import('../lib/env');
            const refreshResponse = await oauthToken({
              grant_type: 'refresh_token',
              refresh_token: tokens.refreshToken,
              client_id: OAUTH2_CLIENT_ID,
            }) as { access_token: string; refresh_token: string; expires_in: number; id_token?: string };

            if (!refreshResponse || !refreshResponse.access_token) {
              throw new Error('OAuth2 token refresh failed: No access token received');
            }

            if (!refreshResponse.refresh_token) {
              throw new Error('OAuth2 token refresh failed: No refresh token received');
            }

            const newTokens = {
              accessToken: refreshResponse.access_token,
              refreshToken: refreshResponse.refresh_token,
              expiresAt: Date.now() + (refreshResponse.expires_in * 1000),
            };

            // Update stored tokens
            localStorage.setItem('oauth_access_token', newTokens.accessToken);
            localStorage.setItem('oauth_refresh_token', newTokens.refreshToken);
            localStorage.setItem('oauth_token_expiry', newTokens.expiresAt.toString());
            
            // Update ID token if provided in refresh response
            if (refreshResponse.id_token) {
              localStorage.setItem('oauth_id_token', refreshResponse.id_token);
            }

            // Parse new JWT to get updated user info
            const jwtPayload = parseJWT(newTokens.accessToken);
            if (!jwtPayload || !jwtPayload.sub) {
              throw new Error('OAuth2 token refresh failed: Invalid token received');
            }

            const updatedUser: User = {
              id: jwtPayload.sub,
              email: user.email,
              email_verified: user.email_verified,
              name: user.name,
              role: jwtPayload.role,
              roles: jwtPayload.roles || user.roles || [jwtPayload.role],
              is_super_admin: jwtPayload.role === 'super_admin',
              org_id: jwtPayload.org_id,
              locked: user.locked,
              locked_until: user.locked_until,
              created_at: user.created_at,
              updated_at: user.updated_at,
            };

            set({ user: updatedUser, isAuthenticated: true });

            return newTokens.refreshToken;
          };

          setRefreshTokenHandler(refreshTokenHandler);

        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'OAuth2 login failed';
          set({
            error: message,
            loading: false,
            isAuthenticated: false,
            user: null
          });
          throw error;
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        // Don't persist loading/error
      }),
    }
  )
);
